import { useState, useEffect } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import ContentBox from '../ContentBox';
import Table, { Row, Cell, HeadCell, Pagination } from '../Table';
import normalizeData from './utils/normalizeData';
import sliceData from './utils/sliceData';
import sortData from './utils/sortData';

const capitalize = name => name.charAt(0).toUpperCase() + name.slice(1);

const useStyles = makeStyles(
  ({ palette }) => ({
    empty: {
      '& .cncui-table-cell': {
        textAlign: 'center',
        height: '5em',
        fontStyle: 'italic',
        color: palette.cncUI.default.primary,
      },
    },
  }),
  { name: 'CncUI-DataTable' },
);

/*
columnData: {
  field: String
  label: String
  cellProps: Object,
  headerProps: Object,
  sortable: Boolean
  sortValue: (row) => { ... } // not being used by now
  value: (row) => { ... }
}
*/

const DataTable = props => {
  const classes = useStyles();
  const { className, shadow = true, transparent = false } = props;
  const { data = [], columns = [], pagination = true } = props;
  const { sort = '', order = 'asc' } = props;
  const { page = 0, pageSize = 10, rowsPerPage = [5, 10, 15, 20] } = props;
  const { emptyRows = 'No records to display', size = 'normal' } = props;

  const [state, setState] = useState({
    data: normalizeData(data || [], columns),
    sortBy: sort || (columns.find(c => c.sortable) || { field: '' }).field,
    sortOrder: order,
    page,
    pageSize,
  });

  useEffect(() => {
    setState(old => ({ ...old, data: normalizeData(data || [], columns) }));
  }, [data]);

  const modify = data => setState(old => ({ ...old, ...data }));

  const headers = () =>
    columns.map(column => {
      const display = column.label || capitalize(column.field);
      if (column.sortable || column.sortable === undefined) {
        return (
          <HeadCell
            key={`Header-${column.field}`}
            cell-id={`Header-${column.field}`}
            label={display}
            column={column.field}
            sort={state.sortBy}
            order={state.sortOrder}
            onClick={field =>
              modify({
                page: 0,
                sortBy: field,
                sortOrder: state.sortOrder === 'asc' ? 'desc' : 'asc',
              })
            }
            {...(column.headerProps || {})}
          />
        );
      }
      return (
        <Cell
          key={`Header-${column.field}`}
          cell-id={`Header-${column.field}`}
          {...(column.headerProps || {})}
        >
          {display}
        </Cell>
      );
    });

  const rows = sliceData(
    state.page,
    state.pageSize,
    sortData(state.sortBy, state.sortOrder, state.data),
  );

  return (
    <ContentBox
      dense
      shadow={shadow}
      transparent={transparent}
      className={clsx('cncui-data-table', className)}
    >
      <Table zebra hover size={size}>
        <Table.Head>
          <Row>{headers()}</Row>
        </Table.Head>
        <Table.Body>
          {state.data.length === 0 && emptyRows ? (
            <Row className={clsx('cncui-data-table-empty-rows', classes.empty)}>
              <Cell colSpan={columns.length}>{emptyRows}</Cell>
            </Row>
          ) : null}
          {rows.map((row, i) => (
            <Row key={`Row-${i}`}>
              {columns.map(column => (
                <Cell
                  key={`Row-${i}-Cell-${column.field}`}
                  cell-id={`Cell-${column.field}`}
                  {...(column.cellProps || {})}
                >
                  {row[column.field].value}
                </Cell>
              ))}
            </Row>
          ))}
        </Table.Body>
      </Table>
      {pagination && (
        <Pagination
          rowsPerPageOptions={rowsPerPage}
          rowsPerPage={state.pageSize}
          component="div"
          count={state.data.length}
          page={state.page}
          onChangeRowsPerPage={e => modify({ pageSize: e.target.value })}
          onChangePage={(_, page) => modify({ page })}
        />
      )}
    </ContentBox>
  );
};

export default DataTable;
